import React, { useEffect, useState } from 'react';
import { Badge, Table } from 'react-bootstrap';
import { History, Info } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

/**
 * Momsregistrering och periodicitet — historik (Gate A decision D9, delivered
 * minimally per B3).
 *
 * READ-ONLY on purpose. `vat_registration_events` is append-only history that
 * period generation reads to answer "what cadence was decided FOR this period",
 * so an edit here would change a already-filed period's periodicity after the
 * fact. Editing is a deferred decision, stated in the panel rather than left
 * for the user to wonder about.
 *
 * Renders nothing when there is no history and no dispens — a tenant that has
 * never had a cadence decision recorded has nothing to read.
 */

const EVENT_LABELS = {
    registered: 'Momsregistrerad',
    deregistered: 'Avregistrerad',
    cadence: 'Beslut om redovisningsperiod',
};

const CADENCE_LABELS = {
    monthly: 'Månadsvis',
    quarterly: 'Kvartalsvis',
    yearly: 'Årsvis',
};

export default function VatRegistrationHistorySection({ companyId }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        axios.get(getApiUrl(`${companyId}/reporting/vat-registration-history`), { withCredentials: true })
            .then((response) => { if (!cancelled && response.data?.success) setData(response.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [companyId]);

    if (!data) return null;
    if ((data.events || []).length === 0 && !data.dispens?.granted) return null;

    return (
        <div className="modern-card" id="momsregistrering">
            <div className="modern-card-header">
                <div>
                    <h2 className="section-title">
                        <History size={16} />
                        Momsregistrering och periodicitet
                    </h2>
                    <p className="section-subtitle">
                        Besluten som momsperioderna genereras utifrån. Historiken är skrivskyddad.
                    </p>
                </div>
            </div>

            <div className="modern-card-body">
                {data.dispens?.granted && (
                    <div className="alert alert-light d-flex align-items-start">
                        <Info size={18} className="me-2 mt-1 flex-shrink-0" />
                        <div>
                            <strong>Dispens för brutet räkenskapsår är registrerad.</strong>{' '}
                            Skatteverket har medgett annat räkenskapsår än kalenderåret
                            (bokföringslagen 3 kap. 2 §).
                            {data.dispens.note && <div className="mt-1">{data.dispens.note}</div>}
                            {data.dispens.sourceUrl && (
                                <div className="mt-1">
                                    <a href={data.dispens.sourceUrl} target="_blank" rel="noopener noreferrer">
                                        Läs mer hos Skatteverket
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {(data.events || []).length > 0 && (
                    <div className="modern-table-container">
                        <Table responsive className="modern-table">
                            <thead>
                                <tr>
                                    <th>Gäller från</th>
                                    <th>Beslut</th>
                                    <th>Periodicitet</th>
                                    <th>Beslutat</th>
                                    <th>Anteckning</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.events.map((event) => (
                                    <tr key={event.event_id}>
                                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>{event.effective_from}</td>
                                        <td>
                                            <Badge bg="" style={{ background: 'var(--rgba-primary-8, #2f5e37)' }}>
                                                {EVENT_LABELS[event.event_type] || event.event_type}
                                            </Badge>
                                        </td>
                                        <td>{event.cadence ? (CADENCE_LABELS[event.cadence] || event.cadence) : '—'}</td>
                                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>{event.decided_at || '—'}</td>
                                        <td>{event.source_note || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                <p className="section-subtitle mt-2">{data.editableNote}</p>
            </div>
        </div>
    );
}
